import dayjs from "dayjs";
import { BadgeCheck, ExternalLink, Instagram, MapPin, Share2, Users, MessageCircle, Calendar, Star } from "lucide-react";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { unwrapApiRecord } from "../../shared/utils/apiResponse";
import { resolvePublicAppUrl } from "../../shared/utils/publicAppUrl";
import { useUserContext } from "../../userDashboard/contexts/userContext";
import { handleGetPublicOrganizerProfile, handleFollowOrganizer, handleGetOrganizerFollowStatus, handleUnfollowOrganizer } from "../../userDashboard/userControllers/organizerController";
import LoadingGrid from "../../utils/loadingGrid";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

interface OrganizerGame {
    id: string | number;
    title: string;
    date: string;
    start_time?: string;
    end_time?: string;
    price?: number;
    currency_code?: string;
    country_code?: string;
    state?: string;
    city?: string;
    pitch?: {
        name: string;
    };
    game_tags?: string[];
}

interface OrganizerProfileData {
    organizer: {
        id: string | number;
        name: string;
        username?: string;
        image?: string;
        profile: {
            city?: string;
            state?: string;
            country_code?: string;
            organizer_type?: string;
            bio?: string;
            experience_level?: string;
            experience?: string;
            expected_games_per_week?: string | number;
            pitch_types?: string[];
            social_links?: {
                instagram?: string;
                twitter?: string;
                snapchat?: string;
                google_reviews?: string;
                whatsapp_group?: string;
            };
            qualifications?: string[];
        };
    };
    games: OrganizerGame[];
    stats?: {
        followers_count?: number;
        total_games?: number;
        upcoming_games?: number;
        average_rating?: number;
        reviews_count?: number;
        cancellation_rate?: number;
        badges?: {
            key?: string;
            title: string;
            description?: string;
        }[];
    };
}

const OrganizerPublicProfile: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { enqueueSnackbar } = useSnackbar();
    const { userProfile } = useUserContext();

    const [profileData, setProfileData] = useState<OrganizerProfileData | null>(null);
    const [loading, setLoading] = useState(true);
    const [followLoading, setFollowLoading] = useState(false);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followersCount, setFollowersCount] = useState(0);

    useEffect(() => {
        const fetchProfile = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const response = await handleGetPublicOrganizerProfile(id);
                const payload = unwrapApiRecord<OrganizerProfileData>(response);
                const stats = payload.stats ?? {};
                setProfileData(payload);
                setFollowersCount(Number(stats.followers_count || 0));
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [id]);

    useEffect(() => {
        const fetchFollowStatus = async () => {
            if (!profileData?.organizer?.id || !userProfile) return;
            try {
                const response = await handleGetOrganizerFollowStatus(String(profileData.organizer.id));
                const payload = unwrapApiRecord<{ is_following?: boolean; followers_count?: number }>(response);
                setIsFollowing(Boolean(payload?.is_following));
                if (typeof payload?.followers_count === "number") {
                    setFollowersCount(payload.followers_count);
                }
            } catch (error) {
                // ignore follow status errors for unauthenticated users
            }
        };

        fetchFollowStatus();
    }, [profileData?.organizer?.id, userProfile]);

    const organizer = profileData?.organizer;
    const organizerProfile = organizer?.profile || {};
    const games = profileData?.games || [];
    const stats = profileData?.stats || {};
    const isOwnProfile = Boolean(userProfile && organizer?.id && userProfile.id === organizer.id);
    const averageRating = typeof stats?.average_rating === "number" ? stats.average_rating : null;
    const reviewsCount = typeof stats?.reviews_count === "number" ? stats.reviews_count : 0;
    const cancellationRate = typeof stats?.cancellation_rate === "number" ? stats.cancellation_rate : null;
    const badges = Array.isArray(stats?.badges) ? stats.badges : [];
    const verifiedBadge = badges.find((badge) => badge?.key === "verified");
    const featuredBadges = badges.filter((badge) => badge?.key !== "verified");
    const qualificationBadges = Array.isArray(organizerProfile.qualifications)
        ? organizerProfile.qualifications.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
        : [];

    const organizerLocation = [organizerProfile.city, organizerProfile.state, organizerProfile.country_code]
        .filter(Boolean)
        .join(", ");

    const formatLabel = (value?: string) => {
        if (!value) return "";
        return value
            .toString()
            .replace(/[_-]+/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const renderStars = (rating: number) => {
        const rounded = Math.round(rating);
        return Array.from({ length: 5 }).map((_, index) => (
            <Star
                key={`rating-star-${index}`}
                className={`h-4 w-4 ${index < rounded
                    ? "text-amber-400 fill-amber-400"
                    : "text-slate-200"
                    }`}
            />
        ));
    };

    const instagramUrl = organizerProfile.social_links?.instagram
        ? organizerProfile.social_links.instagram.startsWith("http")
            ? organizerProfile.social_links.instagram
            : `https://instagram.com/${organizerProfile.social_links.instagram.replace(/^@/, "")}`
        : "";
    const twitterUrl = organizerProfile.social_links?.twitter
        ? organizerProfile.social_links.twitter.startsWith("http")
            ? organizerProfile.social_links.twitter
            : `https://x.com/${organizerProfile.social_links.twitter.replace(/^@/, "")}`
        : "";
    const snapchatUrl = organizerProfile.social_links?.snapchat
        ? organizerProfile.social_links.snapchat.startsWith("http")
            ? organizerProfile.social_links.snapchat
            : `https://www.snapchat.com/add/${organizerProfile.social_links.snapchat.replace(/^@/, "")}`
        : "";

    const organizerPageUrl = useMemo(() => {
        if (!organizer?.id) return "";
        return `${resolvePublicAppUrl()}/organizers/${organizer.id}`;
    }, [organizer?.id]);

    const shareOrganizer = () => {
        if (!organizerPageUrl) return;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard
                .writeText(organizerPageUrl)
                .then(() => enqueueSnackbar("Organizer link copied", { variant: "success" }))
                .catch(() => enqueueSnackbar("Could not copy link", { variant: "error" }));
            return;
        }
        try {
            const textarea = document.createElement("textarea");
            textarea.value = organizerPageUrl;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const success = document.execCommand("copy");
            document.body.removeChild(textarea);
            if (success) {
                enqueueSnackbar("Organizer link copied", { variant: "success" });
            } else {
                enqueueSnackbar("Could not copy link", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Copy not supported in this browser", { variant: "warning" });
        }
    };

    const handleFollowToggle = async () => {
        if (!organizer?.id) return;
        if (!userProfile) {
            enqueueSnackbar("Please sign in to follow organizers", { variant: "info" });
            return;
        }
        if (userProfile?.id === organizer.id) {
            enqueueSnackbar("You cannot follow yourself", { variant: "warning" });
            return;
        }

        setFollowLoading(true);
        try {
            if (isFollowing) {
                const response = await handleUnfollowOrganizer(String(organizer.id));
                const payload = unwrapApiRecord<{ followers_count?: number }>(response);
                setIsFollowing(false);
                if (typeof payload?.followers_count === "number") {
                    setFollowersCount(payload.followers_count);
                }
                enqueueSnackbar("Organizer unfollowed", { variant: "success" });
            } else {
                const response = await handleFollowOrganizer(String(organizer.id));
                const payload = unwrapApiRecord<{ followers_count?: number }>(response);
                setIsFollowing(true);
                if (typeof payload?.followers_count === "number") {
                    setFollowersCount(payload.followers_count);
                }
                enqueueSnackbar("Organizer followed", { variant: "success" });
            }
        } catch (error: unknown) {
            const err = error as { message?: string };
            enqueueSnackbar(err?.message || "Unable to update follow status", { variant: "error" });
        } finally {
            setFollowLoading(false);
        }
    };

    const buildShareUrl = (game: OrganizerGame) => {
        const baseUrl = resolvePublicAppUrl();
        const params = new URLSearchParams();
        params.set("id", String(game.id));
        if (game.title) params.set("title", game.title);
        if (game.country_code) params.set("country", game.country_code);
        if (game.state) params.set("state", game.state);
        if (game.city) params.set("city", game.city);
        return `${baseUrl}/detailed-match?${params.toString()}`;
    };

    const shareGameToWhatsApp = (game: OrganizerGame) => {
        const dateLabel = game.date ? dayjs(game.date).format("ddd, D MMM YYYY") : "Upcoming";
        const timeLabel = [game.start_time, game.end_time].filter(Boolean).join(" - ");
        const priceLabel = game.price ? `Price: ${game.currency_code || ""} ${game.price}`.trim() : null;
        const shareMessage = [
            game.title,
            `${dateLabel}${timeLabel ? ` • ${timeLabel}` : ""}`,
            priceLabel,
            buildShareUrl(game),
        ]
            .filter(Boolean)
            .join("\n");
        const url = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
        globalThis.open(url, "_blank", "noopener,noreferrer");
    };

    const copyGameLink = async (game: OrganizerGame) => {
        const link = buildShareUrl(game);
        if (navigator?.clipboard?.writeText) {
            try {
                await navigator.clipboard.writeText(link);
                enqueueSnackbar("Game link copied", { variant: "success" });
                return;
            } catch (error) {
                // fall back below
            }
        }

        try {
            const textarea = document.createElement("textarea");
            textarea.value = link;
            textarea.style.position = "fixed";
            textarea.style.opacity = "0";
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            const success = document.execCommand("copy");
            document.body.removeChild(textarea);
            if (success) {
                enqueueSnackbar("Game link copied", { variant: "success" });
            } else {
                enqueueSnackbar("Could not copy link", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Could not copy link", { variant: "error" });
        }
    };

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="py-24">
                    <LoadingGrid count={1} />
                </div>
            </>
        );
    }

    if (!organizer) {
        return (
            <>
                <Navbar />
                <div className="h-screen flex items-center justify-center font-Manrope font-black text-gray-400 text-2xl italic">
                    Organizer not found.
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white font-Manrope">
                <div
                    style={{
                        backgroundImage: `url(${landingAsset("herobg.svg")})`,
                        backgroundSize: "cover",
                    }}
                    className="relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#082214]/90 via-[#0b2a1d]/80 to-transparent" />
                    <div className="max-w-7xl mx-auto px-4 md:px-14 py-20 relative">
                        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
                            <div className="space-y-6">
                                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-xs uppercase tracking-[0.3em] font-black rounded-full border border-white/20">
                                    Organizer profile
                                </span>
                                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tighter leading-[0.85]">
                                    {organizer?.name || "Organizer"}
                                </h1>
                                {verifiedBadge && (
                                    <div className="inline-flex items-center gap-2 rounded-full border border-[#B7E7CF] bg-[#ECFDF3] px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#027A48]">
                                        <BadgeCheck className="h-4 w-4" />
                                        {verifiedBadge.title}
                                    </div>
                                )}
                                <div className="flex flex-wrap items-center gap-4 text-white/70 text-sm font-semibold">
                                    {organizerLocation && (
                                        <span className="inline-flex items-center gap-2">
                                            <MapPin className="w-4 h-4" />
                                            {organizerLocation}
                                        </span>
                                    )}
                                    {organizerProfile.organizer_type && (
                                        <span className="px-3 py-1 rounded-full bg-white/10 border border-white/20">
                                            {formatLabel(organizerProfile.organizer_type)}
                                        </span>
                                    )}
                                </div>
                                {organizerProfile.bio && (
                                    <p className="text-white/80 text-lg max-w-2xl italic">
                                        {organizerProfile.bio}
                                    </p>
                                )}
                            </div>

                            <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-[32px] p-6 space-y-4 min-w-[280px]">
                                <div className="flex items-center gap-3">
                                    <div className="w-14 h-14 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white font-black text-xl overflow-hidden">
                                        {organizer?.image ? (
                                            <img
                                                src={organizer.image}
                                                alt={organizer?.name || "Organizer"}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            organizer?.name?.charAt(0) || "O"
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-white font-semibold">{organizer?.name}</p>
                                        <p className="text-white/60 text-xs">@{organizer?.username || "saca-organizer"}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-3 text-center text-white">
                                    <div>
                                        <p className="text-lg font-black">{followersCount}</p>
                                        <p className="text-[10px] uppercase tracking-widest text-white/60">Followers</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-black">{stats?.total_games || 0}</p>
                                        <p className="text-[10px] uppercase tracking-widest text-white/60">Games</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-black">{stats?.upcoming_games || 0}</p>
                                        <p className="text-[10px] uppercase tracking-widest text-white/60">Upcoming</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {userProfile ? (
                                        <button
                                            type="button"
                                            onClick={handleFollowToggle}
                                            disabled={followLoading || isOwnProfile}
                                            className={`w-full px-4 py-3 rounded-2xl font-black text-sm transition ${isOwnProfile
                                                ? "bg-white/10 text-white/70 border border-white/20"
                                                : isFollowing
                                                    ? "bg-white/15 text-white border border-white/30"
                                                    : "bg-white text-[#0b2a1d]"
                                                }`}
                                        >
                                            {isOwnProfile
                                                ? "Your profile"
                                                : followLoading
                                                    ? "Please wait"
                                                    : isFollowing
                                                        ? "Following"
                                                        : "Follow"}
                                        </button>
                                    ) : (
                                        <Link
                                            to="/sign-in"
                                            className="w-full px-4 py-3 rounded-2xl font-black text-sm text-center bg-white text-[#0b2a1d]"
                                        >
                                            Sign in
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        onClick={shareOrganizer}
                                        className="w-full px-4 py-3 rounded-2xl font-black text-sm text-white border border-white/30 hover:bg-white/10 transition flex items-center justify-center gap-2"
                                    >
                                        <Share2 className="w-4 h-4" />
                                        Share
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 md:px-14 py-20 space-y-16">
                    <section className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        <div className="bg-[#F9FAFB] border border-[#EAECF0] rounded-[32px] p-8 space-y-3">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#317333] font-black">Experience</p>
                            <h3 className="text-2xl font-black text-[#101828]">{formatLabel(organizerProfile.experience_level || "Growing")}</h3>
                            <p className="text-sm text-[#475467]">Expected games per week: {organizerProfile.expected_games_per_week || "N/A"}</p>
                            {organizerProfile.experience && (
                                <p className="text-sm text-[#667085] italic">{organizerProfile.experience}</p>
                            )}
                        </div>
                        <div className="bg-white border border-[#EAECF0] rounded-[32px] p-8 space-y-3">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#317333] font-black">Pitch Types</p>
                            <div className="flex flex-wrap gap-2">
                                {(organizerProfile.pitch_types || []).length > 0 ? (
                                    (organizerProfile.pitch_types || []).map((type: string) => (
                                        <span key={type} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#ECFDF3] text-[#027A48]">
                                            {formatLabel(type)}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-sm text-[#667085]">Not specified</span>
                                )}
                            </div>
                        </div>
                        <div className="bg-white border border-[#EAECF0] rounded-[32px] p-8 space-y-4">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#317333] font-black">Social proof</p>
                            <div className="space-y-2">
                                {organizerProfile.social_links?.instagram && (
                                    <a
                                        href={instagramUrl}
                                        className="flex items-center gap-2 text-sm font-semibold text-[#101828] hover:text-[#317333]"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Instagram className="w-4 h-4" /> Instagram
                                    </a>
                                )}
                                {organizerProfile.social_links?.twitter && (
                                    <a
                                        href={twitterUrl}
                                        className="flex items-center gap-2 text-sm font-semibold text-[#101828] hover:text-[#317333]"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <ExternalLink className="w-4 h-4" /> X / Twitter
                                    </a>
                                )}
                                {organizerProfile.social_links?.snapchat && (
                                    <a
                                        href={snapchatUrl}
                                        className="flex items-center gap-2 text-sm font-semibold text-[#101828] hover:text-[#317333]"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <MessageCircle className="w-4 h-4" /> Snapchat
                                    </a>
                                )}
                                {organizerProfile.social_links?.google_reviews && (
                                    <a
                                        href={organizerProfile.social_links.google_reviews}
                                        className="flex items-center gap-2 text-sm font-semibold text-[#101828] hover:text-[#317333]"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Users className="w-4 h-4" /> Google Reviews
                                    </a>
                                )}
                                {organizerProfile.social_links?.whatsapp_group && (
                                    <a
                                        href={organizerProfile.social_links.whatsapp_group}
                                        className="flex items-center gap-2 text-sm font-semibold text-[#101828] hover:text-[#317333]"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <MessageCircle className="w-4 h-4" /> WhatsApp Community
                                    </a>
                                )}
                                {!organizerProfile.social_links?.instagram &&
                                    !organizerProfile.social_links?.twitter &&
                                    !organizerProfile.social_links?.snapchat &&
                                    !organizerProfile.social_links?.google_reviews &&
                                    !organizerProfile.social_links?.whatsapp_group && (
                                        <p className="text-sm text-[#667085]">No social links yet.</p>
                                    )}
                            </div>
                        </div>
                    </section>

                    <section className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        <div className="bg-white border border-[#EAECF0] rounded-[32px] p-8 space-y-4">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#317333] font-black">
                                Organizer rating
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="text-4xl font-black text-[#101828]">
                                    {averageRating !== null ? averageRating.toFixed(1) : "New"}
                                </div>
                                <div>
                                    {averageRating !== null ? (
                                        <div className="flex items-center gap-1">
                                            {renderStars(averageRating)}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-[#667085]">No ratings yet</p>
                                    )}
                                    <p className="text-xs text-[#667085] mt-1">
                                        {reviewsCount} reviews
                                    </p>
                                </div>
                            </div>
                            {cancellationRate !== null && (
                                <p className="text-xs text-[#667085]">
                                    Cancellation rate: {cancellationRate}%
                                </p>
                            )}
                        </div>
                        <div className="bg-[#F9FAFB] border border-[#EAECF0] rounded-[32px] p-8 space-y-4">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#317333] font-black">
                                Badges & Qualifications
                            </p>
                            {qualificationBadges.length > 0 || featuredBadges.length > 0 ? (
                                <div className="space-y-4">
                                    {qualificationBadges.length > 0 && (
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-[#667085] mb-2">
                                                Qualifications
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {qualificationBadges.map((qualification) => (
                                                    <span
                                                        key={qualification}
                                                        className="px-3 py-1 rounded-full text-xs font-semibold bg-white border border-[#D0D5DD] text-[#344054]"
                                                    >
                                                        {qualification}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {featuredBadges.length > 0 && (
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-wide text-[#667085] mb-2">
                                                SACA badges
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {featuredBadges.map((badge: { key?: string; title: string; description?: string }) => (
                                                    <span
                                                        key={badge.key || badge.title}
                                                        title={badge.description}
                                                        className="px-3 py-1 rounded-full text-xs font-semibold bg-white border border-[#D0D5DD] text-[#344054]"
                                                    >
                                                        {badge.title}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-[#667085]">
                                    Qualifications and achievement badges appear here as this organizer builds their profile.
                                </p>
                            )}
                        </div>
                    </section>

                    <section className="space-y-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                            <div>
                                <h2 className="text-3xl md:text-4xl font-black text-[#101828]">Upcoming games</h2>
                                <p className="text-sm text-[#667085]">Share these matches and grow your player base.</p>
                            </div>
                        </div>

                        {games.length === 0 ? (
                            <div className="bg-[#F9FAFB] border border-dashed border-[#EAECF0] rounded-[32px] p-10 text-center text-[#667085]">
                                No upcoming games yet.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {games.map((game: OrganizerGame) => (
                                    <div key={game.id} className="bg-white border border-[#EAECF0] rounded-[28px] p-6 shadow-sm space-y-4">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <p className="text-xs uppercase tracking-[0.2em] text-[#317333] font-black">{game.game_tags?.[0] || "Match"}</p>
                                                <h3 className="text-xl font-black text-[#101828]">{game.title}</h3>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-[#667085]">Price</p>
                                                <p className="text-lg font-black text-[#101828]">
                                                    {game.price ? `${game.currency_code || ""} ${game.price}`.trim() : "Free"}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-4 text-sm text-[#475467]">
                                            <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{game.date ? dayjs(game.date).format("D MMM YYYY") : "TBC"}</span>
                                            <span>{game.start_time} {game.end_time ? `- ${game.end_time}` : ""}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-3">
                                            <Link
                                                to={`/detailed-match?id=${game.id}`}
                                                className="px-4 py-2 rounded-full bg-[#317333] text-white text-xs font-black uppercase tracking-widest"
                                            >
                                                View
                                            </Link>
                                            <button
                                                type="button"
                                                onClick={() => shareGameToWhatsApp(game)}
                                                className="px-4 py-2 rounded-full border border-[#B7E7CF] bg-[#ECFDF3] text-[#0F9D58] text-xs font-black uppercase tracking-widest"
                                            >
                                                WhatsApp
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => copyGameLink(game)}
                                                className="px-4 py-2 rounded-full border border-[#EAECF0] text-xs font-black uppercase tracking-widest"
                                            >
                                                Copy Link
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>
            <Banner />
            <Footer />
        </>
    );
};

export default OrganizerPublicProfile;
